import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../hooks/useAuth';
import { competitionsService } from '../../services/competitions';
import { errorCodesOf } from '../../services/api-error';
import { guardianshipsService, type HeldWard } from '../../services/guardianships';
import type { EntryDetails, StageParticipation } from '../../types/trophy';
import type { PickableAthlete } from './ChooseAthleteDialog';
import type { EntryRefusal } from './EnterStageDialog';

/** Who an entry is for, as the pages hand it around. */
export interface EntryAthlete {
  user_id: string;
  full_name: string;
}

/**
 * Which question a tap on "enter" turned into:
 *
 * - `confirm` — one athlete, and the entry dialog about them (EnterStageDialog:
 *   the gender and date of birth still missing, the category, the turn);
 * - `pick` — the viewer holds children's accounts, so who it is for has to be
 *   settled first (ChooseAthleteDialog), and picking leads to `confirm`;
 * - `identity` — nobody is signed in, and the answer decides which kind of
 *   account to open (EntryIdentityDialog).
 */
export type StageEntryMode = 'confirm' | 'pick' | 'identity' | null;

/**
 * The ask-then-enter flow behind every "enter this stage" button: one stage
 * pending at a time, the request, the toast when it worked — and, when it did
 * not, the refusal handed to the dialog, which puts it under the field it is
 * about. The pages keep the list and render StageEntryDialogs; this keeps the
 * state between them.
 *
 * It asks the server who the viewer's children are the first time a button is
 * pressed, not on page load: a climber with no children — almost everybody —
 * should pay nothing for a flow that is not theirs, and by the time the
 * dialog has to be drawn one request has had time to land.
 *
 * `onEntered` gets the stage as it now stands — entered, with the entry the
 * server made — and who it was entered for, which a page showing one card per
 * athlete needs: a parent and their child can be in the same circuit, and only
 * one of the two cards has just changed.
 */
export function useStageEntry(
  onEntered: (stage: StageParticipation, athlete?: EntryAthlete | null) => void,
  /**
   * The children this account holds, when the page has already read them to
   * draw them (the circuit's and the stage's pages do — see useGuardianWards).
   * Given, nothing is fetched here and `onWardsChanged` is how this flow tells
   * the page its list is stale: one source of truth, so the picker and the
   * boxes on the page cannot disagree. Left out — the dashboard, where no box
   * needs them — the list is read on the first tap and kept.
   */
  options: { wards?: HeldWard[]; onWardsChanged?: () => void } = {}
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // The same notion of "signed in" the buttons use (`signedIn={!!user}`), so
  // that a button and the dialog behind it can never disagree about whether
  // there is an account to enter with.
  const signedIn = !!user;

  const [pending, setPending] = useState<StageParticipation | null>(null);
  const [mode, setMode] = useState<StageEntryMode>(null);
  const [entering, setEntering] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [refusal, setRefusal] = useState<EntryRefusal | null>(null);

  // Who the entry is for: nobody (the viewer) or the child whose card the
  // button sat on. Held beside the stage so the dialog can name them.
  const [forAthlete, setForAthlete] = useState<EntryAthlete | null>(null);

  // The turn the dialog opens on, when the button sat on that turn's card.
  const [initialRoundId, setInitialRoundId] = useState<string | null>(null);

  // Null until asked for. Kept afterwards: a parent entering three stages in a
  // row is asked once. Unused when the page handed its own list in.
  const [fetched, setFetched] = useState<HeldWard[] | null>(null);
  const given = options.wards;
  const onWardsChanged = options.onWardsChanged;
  const wards = given ?? fetched;

  const close = useCallback(() => {
    setPending(null);
    setMode(null);
    setRefusal(null);
    setInitialRoundId(null);
  }, []);

  const requestEnter = useCallback(
    async (stage: StageParticipation, athlete?: EntryAthlete | null, start: { roundId?: string | null } = {}) => {
      // A card about the viewer themselves names them like any other athlete
      // (the dashboard does), but the dialog should say "enter me", not their
      // own name back at them.
      const named = athlete && String(user?.id) !== athlete.user_id ? athlete : null;
      setForAthlete(named);
      setPending(stage);
      setRefusal(null);
      setInitialRoundId(start.roundId ?? null);

      // The page already knows whose entry this is — the dashboard draws one
      // card per athlete — so there is nobody to choose between.
      if (athlete) {
        setMode('confirm');
        return;
      }

      if (!signedIn) {
        setMode('identity');
        return;
      }

      let held = wards;
      if (held === null) {
        setPreparing(true);
        try {
          held = await guardianshipsService.wards();
          setFetched(held);
        } catch {
          // A flow that is not theirs should not fail theirs: treat an
          // unreadable list as no children and ask the plain question.
          held = [];
          setFetched(held);
        } finally {
          setPreparing(false);
        }
      }

      setMode(held.length > 0 ? 'pick' : 'confirm');
    },
    [signedIn, user?.id, wards]
  );

  const enter = useCallback(
    async (stage: StageParticipation, athlete: EntryAthlete | null, details: EntryDetails) => {
      if (entering) return;
      setEntering(true);
      setRefusal(null);
      try {
        const standing = await competitionsService.enter(stage.slug, athlete?.user_id, details);
        onEntered(standing, athlete);
        toast({
          title: athlete
            ? t('guardian.picker.done', { name: athlete.full_name, stage: standing.name })
            : t('stageEntry.done', { stage: standing.name }),
        });
        // The child's entries have changed, so every list that shows them is
        // stale: the page re-reads, or the picker fetches again next time.
        if (athlete) {
          setFetched(null);
          onWardsChanged?.();
        }
        close();
      } catch (error) {
        // Into the dialog, not a toast behind it: a missing value or a turn
        // goes under its own field (see EnterStageDialog).
        setRefusal({
          codes: errorCodesOf(error),
          message: error instanceof Error ? error.message : t('errors.unexpectedError'),
        });
      } finally {
        setEntering(false);
      }
    },
    [close, entering, onEntered, onWardsChanged, t, toast]
  );

  /** The entry dialog's answer: what it asked for, about whoever it was for. */
  const confirm = useCallback(
    (details: EntryDetails = {}) => {
      if (!pending) return;
      enter(pending, forAthlete, details);
    },
    [enter, forAthlete, pending]
  );

  /**
   * The picker's answer: whoever the carousel was on. Not an entry yet — the
   * same dialog follows for them as for anybody, because a child needs a
   * gender and a class and possibly a turn as much as an adult does.
   */
  const confirmAthlete = useCallback(
    (athlete: PickableAthlete) => {
      if (!pending) return;
      const named = athlete.user_id ? { user_id: athlete.user_id, full_name: athlete.full_name } : null;
      setForAthlete(named);
      setRefusal(null);
      setMode('confirm');
    },
    [pending]
  );

  /** The person changed something the last refusal was about. */
  const clearRefusal = useCallback(() => setRefusal(null), []);

  /**
   * The visitor's answer to "who is climbing?". Either way they leave for a
   * sign-up and the stage comes with them: the slug so the entry can be made
   * on the other side, and where they were standing so they come back to it.
   */
  const chooseIdentity = useCallback(
    (who: 'self' | 'ward') => {
      if (!pending) return;
      const from = `${location.pathname}${location.search}`;
      const stage = { slug: pending.slug, name: pending.name };
      close();

      if (who === 'self') {
        navigate('/sign-up', { state: { from, enterStage: stage } });
      } else {
        navigate(`/sign-up/guardian?stage=${encodeURIComponent(pending.slug)}`, {
          state: { from, stageName: pending.name },
        });
      }
    },
    [close, location.pathname, location.search, navigate, pending]
  );

  const onWardCreated = useCallback(
    (ward: HeldWard) => {
      setFetched((current) => [...(current ?? []), ward]);
      onWardsChanged?.();
    },
    [onWardsChanged]
  );

  const onWardUpdated = useCallback(
    (ward: HeldWard) => {
      setFetched((current) => (current ?? []).map((held) => (held.id === ward.id ? ward : held)));
      onWardsChanged?.();
    },
    [onWardsChanged]
  );

  /**
   * The carousel's slides: the viewer first, then each child — and only those
   * who can still be entered. Somebody already on the start list is left out
   * rather than shown greyed: there is nothing to decide about them, and the
   * page they came from already says they are in.
   */
  const athletes = useMemo<PickableAthlete[]>(() => {
    if (!pending) return [];

    const me: PickableAthlete = {
      user_id: null,
      full_name: user?.full_name || user?.email || '',
      name: user?.name,
      surname: user?.surname,
      avatar_color: user?.avatar_color ?? null,
      avatar_url: user?.avatar_small_url ?? user?.avatar_url ?? null,
    };

    return [
      ...(pending.entered ? [] : [ me ]),
      ...(wards ?? [])
        .filter((ward) => !ward.entries.some((entry) => entry.competition_slug === pending.slug))
        .map((ward) => ({
          user_id: ward.id,
          full_name: ward.full_name,
          name: ward.name,
          surname: ward.surname,
          birth_date: ward.birth_date,
          avatar_color: ward.avatar_color,
        })),
    ];
  }, [pending, user, wards]);

  return {
    pending,
    mode,
    entering,
    preparing,
    forAthlete,
    initialRoundId,
    athletes,
    refusal,
    requestEnter,
    cancel: close,
    confirm,
    confirmAthlete,
    clearRefusal,
    chooseIdentity,
    onWardCreated,
    onWardUpdated,
  };
}

export type StageEntryController = ReturnType<typeof useStageEntry>;

/**
 * Opens the entry dialog for the stage somebody was on their way to enter when
 * they went to sign up ("myself", in EntryIdentityDialog): the sign-up page
 * sends them back here with `resumeEntry: <slug>` in the router state, rather
 * than entering them in the background — gender, date of birth, category and
 * turn are all asked in the dialog, and an entry made behind their back could
 * ask none of it.
 *
 * Once, and only once there is an account and the stage still takes them: the
 * state is cleared as it is read, so a reload or Back does not open it again.
 */
export function useResumeStageEntry(
  requestEnter: StageEntryController['requestEnter'],
  stages: StageParticipation[] | null | undefined
) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const resumed = useRef(false);
  const slug = (location.state as { resumeEntry?: string } | null)?.resumeEntry ?? null;

  useEffect(() => {
    if (!slug || !user || resumed.current) return;
    const stage = stages?.find((candidate) => candidate.slug === slug);
    if (!stage) return;

    resumed.current = true;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    if (stage.can_enter) requestEnter(stage);
  }, [slug, user, stages, requestEnter, navigate, location.pathname, location.search]);
}

/** A circuit's participation with one stage replaced by where it now stands. */
export function withStageStanding<T extends { stages: StageParticipation[]; entered: boolean; entries_count: number }>(
  participation: T,
  standing: StageParticipation
): T {
  const stages = participation.stages.map((stage) => (stage.id === standing.id ? standing : stage));
  const entriesCount = stages.filter((stage) => stage.entered).length;
  return { ...participation, stages, entered: entriesCount > 0, entries_count: entriesCount };
}
